import React from 'react';
import { Box, Card, Paper, Typography, Tooltip, Button, SxProps, Theme } from '@mui/material';

/**
 * Shared page furniture.
 *
 * Extracted from the Chart of Accounts redesign so every module page looks
 * like the same product. Without this each page reinvents its own header
 * sizing, card radius and table treatment, and an ERP ends up feeling like
 * nine applications wearing one sidebar.
 *
 * Rules these encode, carried over from the dashboard work:
 *  - null is never rendered as zero; pass `value={null}` and get an em dash.
 *  - status is never colour alone — StatusDot always pairs a dot with a word.
 *  - type/identity colour is kept separate from status colour, so a green
 *    "ASSET" pill is never mistaken for a "good" signal.
 */

// ---------------------------------------------------------------------------
// Page header
// ---------------------------------------------------------------------------

interface PageHeaderProps {
  title: string;
  /** De-emphasised qualifier, e.g. "— Indian Standard". */
  qualifier?: string;
  subtitle?: string;
  /** Buttons / toggles, right-aligned. */
  actions?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, qualifier, subtitle, actions }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 2,
      flexWrap: 'wrap',
      mb: 3,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 0.25 }}>
        {title}
        {qualifier && (
          <Box component="span" sx={{ color: 'text.disabled', fontWeight: 500 }}>
            {' '}
            {qualifier}
          </Box>
        )}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
    {actions && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>{actions}</Box>}
  </Box>
);

// ---------------------------------------------------------------------------
// Stat cards
// ---------------------------------------------------------------------------

export interface StatCardProps {
  title: string;
  /** null renders as an em dash — "not computable" is not zero. */
  value: React.ReactNode;
  icon: React.ReactNode;
  color: string;
  /** Share of a whole, 0-1. Renders a proportion bar. */
  share?: number;
  /** Replaces the share bar when a page has something better to show. */
  footer?: React.ReactNode;
  onClick?: () => void;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color,
  share,
  footer,
  onClick,
}) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      height: '100%',
      p: 2,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      background: `linear-gradient(160deg, ${color}0f 0%, transparent 60%)`,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform .18s ease, box-shadow .18s ease',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${color}1f` },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: share !== undefined || footer ? 1.5 : 0 }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: color,
          color: '#fff',
          flexShrink: 0,
          boxShadow: `0 4px 12px ${color}52`,
          '& svg': { fontSize: 21 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          title={value === null || value === undefined ? undefined : String(value)}
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.15rem', md: '1.2rem', lg: '1.35rem' },
            fontWeight: 700,
            lineHeight: 1.12,
            color,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value === null || value === undefined ? '—' : value}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.25, display: 'block' }}>
          {title}
        </Typography>
      </Box>
    </Box>

    {share !== undefined && (
      <Tooltip title={`${Math.round(share * 100)}% of total`}>
        <Box>
          <Box sx={{ height: 5, borderRadius: 3, backgroundColor: `${color}1f`, overflow: 'hidden' }}>
            <Box
              sx={{
                width: `${Math.min(Math.max(share, 0) * 100, 100)}%`,
                height: '100%',
                borderRadius: 3,
                backgroundColor: color,
                transition: 'width .4s cubic-bezier(.16,1,.3,1)',
              }}
            />
          </Box>
        </Box>
      </Tooltip>
    )}

    {footer && !share && <Box>{footer}</Box>}
  </Card>
);

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

export const FilterBar: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>{children}</Box>
  </Paper>
);

/** Push everything after it to the right of the filter bar. */
export const FilterSpacer: React.FC = () => <Box sx={{ flexGrow: 1 }} />;

// ---------------------------------------------------------------------------
// Table treatment
// ---------------------------------------------------------------------------

/** Wrapper for any data table on a module page. */
export const TableSurface: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Paper
    elevation={0}
    sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflowX: 'auto', overflowY: 'hidden' }}
  >
    {children}
  </Paper>
);

/** Spread onto every <TableCell> in the <TableHead>. */
export const headerCellSx: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: '0.6875rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  borderBottom: '1px solid',
  borderColor: 'divider',
  py: 1.75,
  whiteSpace: 'nowrap',
};

/** Spread onto every body <TableRow>. */
export const bodyRowSx: SxProps<Theme> = {
  '&:hover': { bgcolor: 'action.hover' },
  '&:last-child td': { border: 0 },
};

// ---------------------------------------------------------------------------
// Cell primitives
// ---------------------------------------------------------------------------

/** Tinted glyph + code, for the leading identity column of a table. */
export const CodeCell: React.FC<{ code: string; tint: string; icon: React.ReactNode }> = ({
  code,
  tint,
  icon,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1.5,
        display: 'grid',
        placeItems: 'center',
        backgroundColor: `${tint}1a`,
        flexShrink: 0,
        '& svg': { fontSize: 17, color: tint },
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.875rem', color: tint }}>
      {code}
    </Typography>
  </Box>
);

/** Identity pill — type, stage, category. NOT for status. */
export const TypePill: React.FC<{ label: string; tint: string }> = ({ label, tint }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      px: 1,
      py: 0.375,
      borderRadius: 1,
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '0.04em',
      color: tint,
      backgroundColor: `${tint}14`,
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

export type StatusTone = 'success' | 'warning' | 'error' | 'neutral' | 'info';

const TONE_COLOR: Record<StatusTone, string> = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
  info: 'info.main',
  neutral: 'text.disabled',
};

/** Dot + word. Never colour alone — the label always carries the meaning. */
export const StatusDot: React.FC<{ label: string; tone: StatusTone }> = ({ label, tone }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: TONE_COLOR[tone], flexShrink: 0 }} />
    <Typography variant="caption" sx={{ fontWeight: 600, color: TONE_COLOR[tone], whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
  </Box>
);

/** Consistent empty state for a table body. */
export const TableEmptyState: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <Box sx={{ py: 6, textAlign: 'center' }}>
    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
      {title}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Box>
);

/**
 * Consistent error state with a Retry action — so a failed fetch never
 * masquerades as an empty table. Use in a page's error branch instead of
 * rendering the empty state (or nothing) when a request rejects.
 */
export const ErrorState: React.FC<{ message?: string; onRetry?: () => void }> = ({ message, onRetry }) => (
  <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: 'error.main' }}>
      Unable to load data
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: onRetry ? 2 : 0 }}>
      {message || 'Something went wrong while fetching this information. Please retry or contact your administrator.'}
    </Typography>
    {onRetry && (
      <Button size="small" variant="outlined" onClick={onRetry}>Retry</Button>
    )}
  </Box>
);
